import React from "react";
import { useRouteError, useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "contexts/SupabaseAuthContext";

// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

// icons
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import BugReportIcon from "@mui/icons-material/BugReport";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LoginIcon from "@mui/icons-material/Login";

// Test component to trigger errors (for development/testing)
export const TestErrorComponent = ({ errorType = "javascript" }) => {
  const triggerError = () => {
    if (errorType === "javascript") {
      throw new Error("This is a test JavaScript error");
    } else if (errorType === "reference") {
      // This will cause a reference error
      undefinedFunction();
    }
  };

  return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Error Boundary Test
      </Typography>
      <Button
        variant="contained"
        color="error"
        onClick={triggerError}
        sx={{ mr: 2 }}
      >
        Trigger JavaScript Error
      </Button>
      <Button
        variant="contained"
        color="warning"
        onClick={() => {
          undefinedFunction();
        }}
      >
        Trigger Reference Error
      </Button>
    </Box>
  );
};

// React Error Boundary for catching JavaScript errors
export class ReactErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Return state object to indicate error
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error caught by boundary:", error, errorInfo);
      console.error("Error stack:", error.stack);
      console.error("Component stack:", errorInfo.componentStack);
    }

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryContent error={this.state.error} isReactError={true} />
      );
    }

    return this.props.children;
  }
}

// Component-level error boundary for individual components
export class ComponentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    console.error("Component error caught:", error, errorInfo);
    console.error("Error stack:", error.stack);
    console.error("Component stack:", errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="h6" color="error" sx={{ mb: 1 }}>
            Something went wrong in this component
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please refresh the page or try again later.
          </Typography>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <Alert severity="error" sx={{ textAlign: "left", mb: 2 }}>
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}
              >
                Error: {this.state.error.message}
                {this.state.error.stack &&
                  `\n\nStack:\n${this.state.error.stack}`}
                {this.state.errorInfo?.componentStack &&
                  `\n\nComponent Stack:\n${this.state.errorInfo.componentStack}`}
              </Typography>
            </Alert>
          )}

          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{ mr: 1 }}
          >
            Reload Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Error Boundary Content Component
const ErrorBoundaryContent = ({ error, isReactError = false }) => {
  const navigate = useNavigate();
  const { logout, currentUser } = useSupabaseAuth();
  const theme = useTheme();

  const is404 = error?.status === 404 || error?.statusText === "Not Found";
  const isNetworkError =
    error?.message?.includes("fetch") || error?.message?.includes("network");
  const isAuthError = error?.status === 401 || error?.status === 403;
  const isReferenceError = error?.name === "ReferenceError";
  const isAuthenticated = !!currentUser;

  const getErrorTitle = () => {
    if (isReactError) {
      if (isReferenceError) return "Variable Not Defined";
      return "Application Error";
    }
    if (is404) return "Page Not Found";
    if (isNetworkError) return "Connection Error";
    if (isAuthError) return "Authentication Error";
    return "Something Went Wrong";
  };

  const getErrorMessage = () => {
    if (isReactError) {
      if (isReferenceError) {
        return "A variable or function is not defined. This is likely a development error that needs to be fixed.";
      }
      return "Something went wrong in the application. Please try refreshing the page or contact support if the problem persists.";
    }
    if (is404) {
      return "The page you're looking for doesn't exist or has been moved.";
    }
    if (isNetworkError) {
      return "We're having trouble connecting to our servers. Please check your internet connection and try again.";
    }
    if (isAuthError) {
      return "You need to be logged in to access this page. Please sign in again.";
    }
    return "An unexpected error occurred. Our team has been notified and is working to fix this issue.";
  };

  const getErrorIcon = () => {
    if (isReactError) {
      if (isReferenceError)
        return (
          <ErrorOutlineIcon
            sx={{ fontSize: 60, color: theme.palette.warning.main }}
          />
        );
      return (
        <BugReportIcon sx={{ fontSize: 60, color: theme.palette.error.main }} />
      );
    }
    if (is404)
      return (
        <ErrorOutlineIcon
          sx={{ fontSize: 60, color: theme.palette.warning.main }}
        />
      );
    if (isNetworkError)
      return (
        <ErrorOutlineIcon
          sx={{ fontSize: 60, color: theme.palette.error.main }}
        />
      );
    if (isAuthError)
      return (
        <ErrorOutlineIcon
          sx={{ fontSize: 60, color: theme.palette.info.main }}
        />
      );
    return (
      <BugReportIcon sx={{ fontSize: 60, color: theme.palette.error.main }} />
    );
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleGoToSignIn = () => {
    navigate("/sign-in");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/sign-in");
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid
          size={{
            xs: 12,
            md: 8
          }}>
          <Card
            sx={{
              borderRadius: 3,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 3,
                }}
              >
                {/* Error Icon */}
                <Box sx={{ mt: 3, mb: 0 }}>{getErrorIcon()}</Box>

                {/* Error Title */}
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 1,
                  }}
                >
                  {getErrorTitle()}
                </Typography>

                {/* Error Message */}
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 3,
                    maxWidth: 500,
                    lineHeight: 1.6,
                  }}
                >
                  {getErrorMessage()}
                </Typography>

                {/* Error Details (for developers) */}
                {process.env.NODE_ENV === "development" && error && (
                  <Alert
                    severity="info"
                    sx={{
                      width: "100%",
                      maxWidth: 600,
                      mb: 3,
                      textAlign: "left",
                    }}
                  >
                    <AlertTitle>Debug Information</AlertTitle>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}
                    >
                      Error Name: {error.name}
                      Error Message: {error.message}
                      {error.status && `\nStatus: ${error.status}`}
                      {error.statusText && `\nStatus Text: ${error.statusText}`}
                      {error.stack && `\nStack Trace:\n${error.stack}`}
                      {this.state?.errorInfo?.componentStack &&
                        `\nComponent Stack:\n${this.state.errorInfo.componentStack}`}
                    </Typography>
                  </Alert>
                )}

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    width: "100%",
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {isAuthenticated ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<HomeIcon />}
                        onClick={handleGoToDashboard}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          minWidth: 140,
                        }}
                      >
                        Go to Dashboard
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<LogoutIcon />}
                        onClick={handleLogout}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          minWidth: 140,
                        }}
                      >
                        Sign Out
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<LoginIcon />}
                        onClick={handleGoToSignIn}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          minWidth: 140,
                        }}
                      >
                        Sign In
                      </Button>

                      <Button
                        variant="outlined"
                        startIcon={<HomeIcon />}
                        onClick={handleGoHome}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          minWidth: 140,
                        }}
                      >
                        Go Home
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                      minWidth: 140,
                    }}
                  >
                    Refresh Page
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleGoBack}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      fontWeight: 600,
                      minWidth: 140,
                    }}
                  >
                    Go Back
                  </Button>
                </Box>

                {/* Additional Help */}
                <Box
                  sx={{
                    mt: 4,
                    pt: 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      maxWidth: 500,
                    }}
                  >
                    {isAuthenticated
                      ? "If this problem persists, please contact our support team or try signing out and back in."
                      : "If this problem persists, please contact our support team or try refreshing the page."}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

// Main Error Boundary Component (for routing errors)
const ErrorBoundary = () => {
  const error = useRouteError();
  return <ErrorBoundaryContent error={error} />;
};

export default ErrorBoundary;
